import React from 'react';
import { Button, Tooltip } from '@mui/material';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { SCOPES } from '@/features/config/scopes';
import { useAdminConfirm } from '../../shared/AdminConfirmProvider';
import { useResetFormFieldConfigsMutation } from '../api/formFieldConfigsApi';
import { useFormFieldConfigScope } from '../useFormFieldConfigScope';

/**
 * Resets the ticked rows that belong to the page's ledger or broker group (Source is that
 * scope), after the admin confirms: their configs are deleted, so the fields follow the next
 * scope in line again. Rows inherited from another scope are left alone, so resetting from a
 * broker group never deletes its ledger's config. Shown only when a ticked row can be reset,
 * so never for the default.
 */
export const ResetFormFieldConfigsButton = ({ configs, disabled }) => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const { scope } = useFormFieldConfigScope();
	const [resetFormFieldConfigs, { isLoading }] = useResetFormFieldConfigsMutation();

	const docIds = configs
		.filter((config) => config.scope === scope.scope && config.scopeId === scope.scopeId)
		.map((config) => config.id);
	// Shown only when a ticked row can be reset (never on the default)
	if (scope.scope === SCOPES.DEFAULT || !docIds.length) return null;

	const handleReset = async () => {
		const skipped = configs.length - docIds.length;
		const confirmed = await confirm({
			title: `Reset ${docIds.length} field${docIds.length === 1 ? '' : 's'}?`,
			content: `These fields will follow the ${scope.scope === SCOPES.BROKER_GROUP ? 'ledger or ' : ''}default again, and their settings here are deleted.${skipped ? ` ${skipped} ticked field${skipped === 1 ? ' already follows' : 's already follow'} another scope and won't change.` : ''}`,
			confirmText: 'Reset',
		});
		if (!confirmed) return;

		const result = await resetFormFieldConfigs({ ...scope, docIds });
		if (result.error) {
			dispatch(
				showToast({ message: "Couldn't reset the config. Please try again.", error: true }),
			);
			return;
		}
		dispatch(
			showToast({
				message: 'Reset the config',
				success: true,
				duration: 4000,
				autohide: true,
			}),
		);
	};

	return (
		<Tooltip title='Follow the ledger or default again'>
			<span>
				<Button
					variant='table_actions'
					disabled={disabled || isLoading}
					onClick={handleReset}
				>
					{`Reset to default (${docIds.length})`}
				</Button>
			</span>
		</Tooltip>
	);
};

ResetFormFieldConfigsButton.displayName = 'ResetFormFieldConfigsButton';
